import React, { useState, useRef } from 'react';
import { Dialog, IconButton, Typography, Box } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useTheme } from '../../ThemeContext';

const BulkUploadModal = ({ open, onClose, onUpload }) => {
  const { themeMode, currentTheme } = useTheme();
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  // Handle drag events
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  // Handle drop
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  // Handle file selection
  const handleFile = (file) => {
    // Check if file is Excel format
    const validTypes = [
      'application/vnd.ms-excel', // .xls
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.ms-excel.sheet.macroEnabled.12', // .xlsm
      'application/vnd.ms-excel.template.macroEnabled.12' // .xltx
    ];
    
    const validExtensions = ['.xls', '.xlsx', '.xlsm', '.xlt'];
    const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    
    if (validTypes.includes(file.type) || validExtensions.includes(fileExtension)) {
      setSelectedFile(file);
      if (onUpload) {
        onUpload(file);
      }
      // You can add API call here to upload the file
      console.log('File selected:', file);
    } else {
      alert('Please upload only Excel files (.xls, .xlsx, .xlsm, .xlt)');
    }
  };

  // Trigger file input click
  const handleClickUpload = () => {
    fileInputRef.current?.click();
  };

  // Handle file input change
  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: {
          backgroundColor: themeMode === 'dark' ? '#1B1A33' : '#ffffff',
          borderRadius: '12px',
          padding: '0'
        }
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '20px 24px',
          borderBottom: `1px solid ${themeMode === 'dark' ? '#302d4c' : '#e0e0e0'}`
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontSize: '20px',
            fontWeight: '600',
            color: themeMode === 'dark' ? '#ffffff' : '#000000'
          }}
        >
          Upload provider details in bulk
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            color: themeMode === 'dark' ? '#ffffff' : '#000000',
            padding: '4px'
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Upload Area */}
      <Box sx={{ padding: '32px 24px' }}>
        <Box
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={handleClickUpload}
          sx={{
            border: `2px dashed ${dragActive ? '#FF6B47' : (themeMode === 'dark' ? '#302d4c' : '#e0e0e0')}`,
            borderRadius: '8px',
            padding: '60px 40px',
            textAlign: 'center',
            cursor: 'pointer',
            backgroundColor: dragActive 
              ? (themeMode === 'dark' ? 'rgba(255, 107, 71, 0.05)' : 'rgba(255, 107, 71, 0.02)') 
              : (themeMode === 'dark' ? '#04002b' : '#f5f5f5'),
            transition: 'all 0.3s ease'
          }}
        >
          {/* Excel Icon */}
          <Box
            sx={{
              width: '80px',
              height: '80px',
              margin: '0 auto 20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <svg width="80" height="80" viewBox="0 0 80 80" fill="none">
              <path d="M50 5H15C13.3431 5 12 6.34315 12 8V72C12 73.6569 13.3431 75 15 75H65C66.6569 75 68 73.6569 68 72V23L50 5Z" 
                    fill={themeMode === 'dark' ? '#ffffff' : '#000000'} 
                    fillOpacity="0.1" 
                    stroke={themeMode === 'dark' ? '#ffffff' : '#000000'} 
                    strokeWidth="2"/>
              <path d="M50 5V20C50 21.6569 51.3431 23 53 23H68" 
                    stroke={themeMode === 'dark' ? '#ffffff' : '#000000'} 
                    strokeWidth="2"/>
              <rect x="28" y="38" width="24" height="20" rx="2" fill="#FF4444"/>
              <text x="40" y="52" textAnchor="middle" fill="white" fontSize="10" fontWeight="bold">XLS</text>
              <rect x="25" y="35" width="6" height="4" fill={themeMode === 'dark' ? '#ffffff' : '#000000'} fillOpacity="0.2"/>
              <rect x="34" y="35" width="6" height="4" fill={themeMode === 'dark' ? '#ffffff' : '#000000'} fillOpacity="0.2"/>
              <rect x="43" y="35" width="6" height="4" fill={themeMode === 'dark' ? '#ffffff' : '#000000'} fillOpacity="0.2"/>
            </svg>
          </Box>

          {/* Text */}
          <Typography
            sx={{
              fontSize: '14px',
              color: themeMode === 'dark' ? '#ffffff' : '#000000',
              marginBottom: '8px'
            }}
          >
            Drag & drop excel (.xls, .xlsx, .xlms, .xlt )
          </Typography>
          <Typography
            sx={{
              fontSize: '14px',
              color: themeMode === 'dark' ? '#8B8FA8' : '#666666',
              marginBottom: '16px'
            }}
          >
            files to upload
          </Typography>

          {/* OR Divider */}
          <Typography
            sx={{
              fontSize: '14px',
              color: themeMode === 'dark' ? '#8B8FA8' : '#666666',
              margin: '16px 0'
            }}
          >
            or
          </Typography>

          {/* Click to upload link */}
          <Typography
            sx={{
              fontSize: '14px',
              color: '#FF6B47',
              cursor: 'pointer',
              fontWeight: '500',
              '&:hover': {
                textDecoration: 'underline'
              }
            }}
          >
            Click to upload files
          </Typography>

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".xls,.xlsx,.xlsm,.xlt"
            onChange={handleFileInputChange}
            style={{ display: 'none' }}
          />
        </Box>

        {/* Selected file display */}
        {selectedFile && (
          <Box
            sx={{
              marginTop: '16px',
              padding: '12px',
              backgroundColor: themeMode === 'dark' ? '#04002b' : '#f5f5f5',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <Typography
              sx={{
                fontSize: '14px',
                color: themeMode === 'dark' ? '#ffffff' : '#000000'
              }}
            >
              {selectedFile.name}
            </Typography>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedFile(null);
              }}
              sx={{ color: themeMode === 'dark' ? '#ffffff' : '#000000' }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Box>
    </Dialog>
  );
};

export default BulkUploadModal;
